import { DomainName } from "client/Api/SecurityConfiguration/securityConfiguration";
import {
	IconButton,
	InputRow,
	TextInput,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { useCallbackWithoutArguments } from "client/Hooks";
import {
	getClientId,
	ObjectWithClientId,
	setClientId
} from "client/Misc/clientId";
import { generateTrackingId, setTrackingId } from "client/Misc/utils";
import { ReactElement, useCallback } from "react";
import styles from "./DomainNameCollection.module.less";

export type DomainNameCollectionProps = {
	attachable: IValueInputAttachable<readonly ObjectWithClientId<DomainName>[]>;
	maxAmount: number;
};

export const DomainNameCollection = (props: DomainNameCollectionProps): ReactElement => {
	const {
		attachable,
		maxAmount
	} = props;

	const addHandler = useCallback(
		(): void => {
			const newIpAddress: ObjectWithClientId<DomainName> = setClientId({ value: "" });

			attachable
				.reducer
				.push(newIpAddress);

			attachable.validation
				.triggerInputFinished();
		},
		[attachable]
	);

	const removeHandler = useCallback(
		(index: number): void => {
			attachable
				.reducer
				.removeAt(index);

			attachable.validation
				.triggerInputFinished();
		},
		[attachable]
	);

	const trackingId: string = attachable.reducer
		.path;

	return (
		<div {...setTrackingId(trackingId)}>
			<ul className={styles.list}>
				{attachable.map((x, i) => (
					<li
						key={getClientId(x.value)}
						className={styles.listItem}
					>
						<Entry
							attachable={x}
							onRemove={() => removeHandler(i)}
						/>
					</li>
				))}
			</ul>

			{attachable.value.length < maxAmount && (
				<IconButton
					kind="dashed"
					width="fill"
					icon="plus"
					onClick={addHandler}
					trackingId={generateTrackingId(trackingId, "__add")}
				/>
			)}
		</div>
	);
};
DomainNameCollection.displayName = "DomainNameCollection";

type EntryProps = {
	attachable: IValueInputAttachable<ObjectWithClientId<DomainName>>;
	onRemove: () => void;
};

const Entry = (props: EntryProps): ReactElement => {
	const {
		attachable,
		onRemove
	} = props;

	const removeHandler: () => void = useCallbackWithoutArguments(onRemove);

	const valueAttachable: IValueInputAttachable<string> = attachable.for("value");

	return (
		<div>
			<InputRow>
				<InputRow.Stretch>
					<TextInput
						{...valueAttachable.attachToTextInput()}
						placeholder="example.com"
						autoFocus={
							valueAttachable.value === ""
								? "select"
								: undefined
						}
					/>
				</InputRow.Stretch>

				<IconButton
					kind="secondary"
					icon="minus"
					attachedOn="left"
					onClick={removeHandler}
					trackingId={generateTrackingId(
						valueAttachable.reducer
							.path,
						"__remove"
					)}
				/>
			</InputRow>

			<ValidationError {...valueAttachable.attachToValidationError()} />
		</div>
	);
};
Entry.displayName = "Entry";
